import { useState } from 'react';

import { actionLog } from 'services/actionlog.service';
import {
  deleteFeedbackByQuery,
  getNextManagers,
  getServerTime,
  getTasksManagerMad,
  updateTask,
} from 'services/dashboard.service';
import { User } from 'types/user';
import { CheckManagerType } from 'utils/strings';

import { MadRuleContext, madUserCodEtapa } from './context';
import { makeGuardBatch, makeGuardTask, shouldGuardBatchOnDouble } from './concurrency';
import { MadQuotas, selectCascadeReversal, selectCascadeTargets } from './quotas';
import { canApproveParticipantsCount, canPromote, canReset, getTaskStepLocal, isPitStopAssessor } from './rules';
import {
  applyAssessorApprove,
  applyAssessorStop,
  applyPitStopOnPromote,
  resetPitStop41,
  resetStep5,
  shouldEnterPitStop,
} from './pitstop';
import { buildDuplaOptions, resolveSelectionWrite, SelectionWrite } from './succession';
import { DuplaOption } from '../shared/types';

// Orquestração das ações do fluxo MAD — espelho de normal/useNormalActions.ts (que já é
// o port 1:1 do monólito) COM as divergências canônicas da dupla de liderança
// (2026-07-15). Este hook NÃO contém regra de domínio própria: gates vêm de rules.ts,
// cascatas de quotas.ts, concorrência de concurrency.ts e pit stop de pitstop.ts.
//
// Divergências deliberadas vs fluxo Normal (universo exclusivamente MAD, tasks com
// mad === true; a dupla age sobre o MESMO pool em telas separadas):
// - SWAP da dupla no updateAllTasks (CON-05): quando o logado age como PARCEIRO, troca
//   original_manager/evaluator↔non_evaluator para registrar quem de fato agiu
//   (port de index.tsx:2296-2335);
// - guard em lote também na BONIFICAÇÃO (CON-04, shouldGuardBatchOnDouble): há slot de
//   promoção pode estar desatualizado porque o parceiro concorre no mesmo pool;
// - pit stop MAD (PIT-01/03/04) atrás da flag MAD_PITSTOP_ENABLED, via pitstop.ts, com o
//   assessor DUPLA-AWARE (isPitStopAssessor);
// - SelectDupla no lugar do SelectGestor: a escolha do próximo elo é uma DUPLA
//   (evaluator + non_evaluator gravados juntos, SUC-09);
// - o nível efetivo é SEMPRE composto pela dupla (madUserCodEtapa, sem o verifyMad
//   condicional do Normal);
// - guards revalidam contra o aggregate MAD (getTasksManagerMad, CON-07).

// Player do modal SelectDupla (par + níveis de busca + opções já resolvidas).
export interface SelectDuplaState {
  task: any;
  cod: number[];
  duplaOptions: DuplaOption[];
}

export interface MadActionsDeps {
  // Contexto de regras do fluxo MAD — a página DEVE construí-lo com os valores de `quotas`
  // (promotedTasks/maxPromotedTasks etc.), como o monólito fazia com os estados.
  ctx: MadRuleContext;
  data: any[] | undefined;
  filteredData: any[] | undefined;
  // loadData do monólito com os parâmetros da fase fechados: com taskId devolve a task
  // fresca; sem argumento recarrega a lista inteira.
  reload: (taskId?: string) => Promise<any>;
  setPageLoading: (loading: boolean) => void;
  // setShowConcurrentActionModal do monólito.
  showConcurrentModal: (message: string) => void;
  quotas: MadQuotas;
  bonus: any;
  user: User | undefined;
  isVacationManager: boolean;
}

export function useMadActions(deps: MadActionsDeps) {
  const { ctx, filteredData, reload, setPageLoading, showConcurrentModal, quotas, bonus, user, isVacationManager } = deps;

  const playerInfo: any = ctx.playerInfo;
  const userInfo: any = ctx.userInfo;
  const activeStageObj: any = ctx.activeStageObj;
  const { realDatetime, getActiveEvaluationStep } = ctx;

  const [selectedDupla, setSelectedDupla] = useState<SelectDuplaState | undefined>();
  const [functionDupla, setFunctionDupla] = useState<string>('');
  const [getAction, setGetAction] = useState<any>(false);
  const [modalCod5, setModalCod5] = useState<any>(undefined);

  // Port do memo isDirectManager (index.tsx:139-147).
  const isDirectManager =
    isVacationManager &&
    userInfo &&
    playerInfo &&
    (userInfo?._id === playerInfo?.gestor ||
      (Array.isArray(userInfo?.mad_dupla) && userInfo?.mad_dupla?.some((md: any) => md._id && md._id === playerInfo?.gestor)));

  const boundCanReset = (task: any) => canReset(ctx, task);

  const guardTaskAgainstConcurrentUpdate = makeGuardTask({ reload, onConflict: showConcurrentModal });

  // CON-07: o guard em lote revalida contra o MESMO aggregate do fluxo (tasks_manager_mad).
  const guardBatchAgainstConcurrentUpdate = makeGuardBatch({
    fetchAll: () =>
      getTasksManagerMad(
        ctx.internalUser?.extra?.id,
        activeStageObj?.date_start,
        activeStageObj?.date_finish,
        activeStageObj?.step,
      ),
    onConflict: showConcurrentModal,
  });

  // Grava a DUPLA escolhida (evaluator + non_evaluator, SUC-09). O gestor entra em
  // evaluator; o parceiro (quando houver) em non_evaluator — funcionalmente equivalentes
  // (a dupla age como um, R-21) e o swap CON-05 registra quem de fato agiu.
  function writeSelection(updatedData: any, selection: SelectionWrite | undefined) {
    if (!Array.isArray(updatedData.evaluator)) {
      updatedData.evaluator = [];
    }

    updatedData.evaluator.push(selection?.evaluatorId);

    if (selection?.nonEvaluatorId) {
      if (!Array.isArray(updatedData.non_evaluator)) {
        updatedData.non_evaluator = [];
      }

      updatedData.non_evaluator.push(selection.nonEvaluatorId);
    }
  }

  // Espelho de normal/updateAllTasks (port de index.tsx:2288-2463) COM o bloco isDuo de
  // swap da dupla (index.tsx:2296-2335) que o fluxo Normal não tem.
  async function updateAllTasks(action: string, nObj: any, oldObj?: any) {
    let taskId;

    try {
      setPageLoading(true);

      const newObj = JSON.parse(JSON.stringify(nObj));

      // CON-05 — SWAP da dupla: quando o logado age como PARCEIRO (aparece em
      // non_evaluator_detail com o gestor da task na sua mad_dupla), troca os papéis para
      // registrar que QUEM AGIU foi ele. Port fiel de index.tsx:2296-2335.
      const isDuo = {
        original_manager:
          Array.isArray(newObj.non_evaluator_detail) &&
          Array.isArray(newObj.non_evaluator_detail.find((x: any) => x._id === playerInfo._id)?.mad_dupla) &&
          !!newObj.non_evaluator_detail
            .find((x: any) => x._id === playerInfo._id)
            ?.mad_dupla.find((md: any) => md._id && md._id === String(newObj.original_manager)),
        evaluator:
          Array.isArray(newObj.non_evaluator_detail) &&
          Array.isArray(newObj.non_evaluator_detail.find((x: any) => x._id === playerInfo._id)?.mad_dupla) &&
          Array.isArray(newObj.evaluator) &&
          !!newObj.evaluator.find(
            (ev: any) =>
              !!newObj.non_evaluator_detail
                .find((x: any) => x._id === playerInfo._id)
                ?.mad_dupla?.find((md: any) => md._id === ev),
          ),
      };

      if (isDuo.original_manager) {
        const index_non_evaluator = newObj.non_evaluator.indexOf(playerInfo._id);
        if (index_non_evaluator !== -1) {
          newObj.non_evaluator[index_non_evaluator] = '' + newObj.original_manager;
          newObj.original_manager = playerInfo._id;
        }
      } else if (isDuo.evaluator) {
        const detail = newObj.non_evaluator_detail.find((x: any) => x._id === playerInfo._id);

        const matchingMadId = Array.isArray(detail?.mad_dupla)
          ? detail.mad_dupla.map((md: any) => md._id).find((id: string) => newObj.evaluator.includes(id))
          : null;

        const index_non_evaluator = newObj.non_evaluator.indexOf(playerInfo._id);
        const index_evaluator = matchingMadId ? newObj.evaluator.indexOf(matchingMadId) : -1;

        if (index_non_evaluator !== -1 && index_evaluator !== -1 && matchingMadId) {
          newObj.non_evaluator[index_non_evaluator] = matchingMadId;
          newObj.evaluator[index_evaluator] = playerInfo._id;
        }
      }

      // Auto-aprovação da quantidade de participantes ao apreciar (PART-02): "Devolver"
      // (reject) e "reset" não aprovam.
      if (!!action.match(/(accept|stop|double|award|promote)/g) && canApproveParticipantsCount(playerInfo, nObj)) {
        newObj.participants_approval = { ...(newObj.participants_approval || {}), status: 'approved' };
        newObj.maxPlayersNumber = Array.isArray(newObj.team) ? newObj.team.length : newObj.maxPlayersNumber;
      }

      if (!!action.match(/(accept|stop|reject|double|award)/g)) {
        taskId = newObj._id;
        await updateTask(newObj);
        return true;
      } else if (!!action.match(/(promote|reset)/g)) {
        if (action === 'promote') {
          if (newObj.prosseguir_idGestor === playerInfo._id) {
            taskId = newObj._id;
            await updateTask(newObj);
            return true;
          } else {
            const _promotedTasks = quotas.promotedTasks.filter((x: any) => x._id !== newObj._id);
            _promotedTasks.push(newObj);

            const cotaReached = _promotedTasks.length >= quotas.maxPromotedTasks;

            // Ao fechar a cota de promoção as demais bonificadas (dobrado) viram concluidoAuto.
            // Validamos contra os dados frescos: se o parceiro promoveu algo que ainda não está
            // nesta tela, cancelamos e recarregamos (CAS-01/CON-03).
            if (cotaReached) {
              const guard = await guardBatchAgainstConcurrentUpdate(ctx, filteredData as any[]);
              if (!guard.ok) {
                return null;
              }
            }

            // CAS-01 — cascata direta.
            const tasks = selectCascadeTargets(ctx, filteredData as any[], newObj, boundCanReset);

            if (tasks.length) {
              await actionLog('me', 'change_task_status', {
                tasks: tasks.map((x: any) => {
                  return {
                    taskId: x._id,
                    status: 'concluidoAuto',
                  };
                }),
              });
            }

            if (quotas.promotedTasks.length >= quotas.maxPromotedTasks) {
              await actionLog('me', 'active_bonus', { task: newObj._id, stage: newObj.stageNumber });
            }

            await updateTask(newObj);
            return true;
          }
        } else {
          if (newObj.prosseguir_idGestor === playerInfo._id) {
            taskId = newObj._id;
            await updateTask(newObj);
            return true;
          } else {
            // CAS-04 — cascata reversa.
            const tasks = selectCascadeReversal(ctx, filteredData as any[], newObj, oldObj, boundCanReset);

            if (
              filteredData?.find(e => e._id === bonus?.taskId && e.stageNumber === bonus.stage) &&
              (oldObj?.status === 'promovido' || oldObj?.status === 'selo')
            ) {
              await actionLog('me', 'remove_action', { id: bonus?.action });
            } else if (tasks.length) {
              await actionLog('me', 'change_task_status', {
                tasks: tasks.map((x: any) => {
                  return {
                    taskId: x._id,
                    status: 'dobrado',
                  };
                }),
              });
            }

            await updateTask(newObj);
            return true;
          }
        }
      }
    } catch (error) {
      console.log(error);
    } finally {
      await reload(taskId);
      setPageLoading(false);
    }

    return null;
  }

  // Espelho de normal/handleReset (port de index.tsx:2465-2656). CAS-05: o guard em lote
  // roda ANTES de qualquer mutação (deleteFeedbackByQuery). Ramos 4.1/5 via pitstop.ts.
  async function handleReset(task: any) {
    if (!canReset(ctx, task)) return false;

    const newObj = await guardTaskAgainstConcurrentUpdate(task);
    if (!newObj) return false;

    if (task.status === 'promovido' || task.status === 'selo') {
      const guard = await guardBatchAgainstConcurrentUpdate(ctx, filteredData as any[]);
      if (!guard.ok) {
        void reload();
        return false;
      }
    }

    // Elos presentes ANTES do reset (doc fresco) — base para sincronizar o
    // non_evaluator com o corte do evaluator ao final.
    const previousEvaluator: string[] = Array.isArray(newObj.evaluator) ? newObj.evaluator.map(String) : [];

    delete newObj.evaluator;

    if (
      (String(task.original_manager) === String(playerInfo._id) ||
        (Array.isArray(playerInfo?.mad_dupla) &&
          playerInfo.mad_dupla.some((md: any) => md._id && md._id === String(task.original_manager)))) &&
      madUserCodEtapa(ctx, null) === 4 &&
      activeStageObj &&
      activeStageObj.date_evaluation_step4_start &&
      realDatetime < activeStageObj.date_evaluation_step4_start
    ) {
      delete newObj.stageNumber;
      delete newObj.multiplicador;
      delete newObj.prosseguir_gst;
      delete newObj.prosseguir_idGestor;
      delete newObj.prosseguir_who_approved;

      newObj.status = 'aguardando';

      await deleteFeedbackByQuery(`taskId:'${newObj._id}'`);
    } else if (
      (String(task.original_manager) === String(playerInfo._id) ||
        (Array.isArray(playerInfo?.mad_dupla) &&
          playerInfo.mad_dupla.some((md: any) => md._id && md._id === String(task.original_manager)))) &&
      madUserCodEtapa(ctx, null) === 5
    ) {
      delete newObj.stageNumber;
      delete newObj.multiplicador;
      delete newObj.prosseguir_gst;
      delete newObj.prosseguir_idGestor;
      delete newObj.prosseguir_who_approved;

      newObj.status = 'aguardando';

      await deleteFeedbackByQuery(`taskId:'${newObj._id}'`);
    } else {
      const _getActiveEvaluationStep =
        isDirectManager && getTaskStepLocal(newObj) && getTaskStepLocal(newObj) !== getActiveEvaluationStep
          ? getTaskStepLocal(newObj)
          : getActiveEvaluationStep;

      if (_getActiveEvaluationStep === 2) {
        delete newObj.stageNumber;
        delete newObj.multiplicador;
        delete newObj.prosseguir_gst;
        delete newObj.prosseguir_idGestor;
        delete newObj.prosseguir_who_approved;

        newObj.status = 'aguardando';

        await deleteFeedbackByQuery(`taskId:'${newObj._id}'`);
      } else {
        const playerId =
          Array.isArray(task.evaluator) && task.evaluator.includes(String(playerInfo._id))
            ? String(playerInfo._id)
            : Array.isArray(playerInfo.mad_dupla) &&
                Array.isArray(task.evaluator) &&
                playerInfo.mad_dupla.some((md: any) => task.evaluator.includes(String(md._id)))
              ? String(playerInfo.mad_dupla.find((md: any) => task.evaluator.includes(String(md._id)))._id)
              : null;

        if (playerId) {
          newObj.evaluator = [];

          for (let index = 0; index < task.evaluator.length; index++) {
            const id = task.evaluator[index];

            newObj.evaluator.push(id);

            if (id === playerId) {
              break;
            }
          }
        }

        if (_getActiveEvaluationStep === 3) {
          delete newObj.prosseguir_gst;
          delete newObj.prosseguir_idGestor;
          delete newObj.prosseguir_who_approved;

          newObj.status = 'aprovado';
          newObj.multiplicador = 1;
          newObj.stageNumber = 1;
        } else if (_getActiveEvaluationStep === 4) {
          delete newObj.prosseguir_gst;
          delete newObj.prosseguir_idGestor;
          delete newObj.prosseguir_who_approved;
          delete newObj.prosseguir_managers;

          newObj.status = 'promovido';
          newObj.multiplicador = 2;
          newObj.stageNumber = 2;
        } else if (_getActiveEvaluationStep === 4.1) {
          // PIT-08 — reabre o pit stop (janela 4.1). resetPitStop41 não mexe em
          // prosseguir_who_approved; o original o apaga (index.tsx:2592), então o
          // removemos explicitamente após o reset.
          resetPitStop41(newObj);
          delete newObj.prosseguir_who_approved;
        } else if (_getActiveEvaluationStep === 5) {
          resetStep5(newObj);
        }

        if (!!newObj.feedback?.length) {
          let evaluators: any;

          if (Array.isArray(newObj.evaluator)) {
            evaluators = [newObj.original_manager, ...newObj.evaluator];
          } else {
            evaluators = [newObj.original_manager];
          }

          const evaluatorsWithoutMe = evaluators.filter((x: string) => x !== String(playerInfo._id));

          const feedbacksWithoutMe = newObj.feedback
            .filter((x: any) => {
              if (evaluatorsWithoutMe?.length) {
                const evaluatorId = x.evaluatorId.replace(/F(0+)?/g, '');
                return !evaluatorsWithoutMe.includes(evaluatorId);
              }

              return true;
            })
            .map((x: any) => x._id);

          if (feedbacksWithoutMe.length) {
            const deletedFeedbacks: any = [];

            feedbacksWithoutMe.forEach((feedbackId: string) =>
              deletedFeedbacks.push(deleteFeedbackByQuery(`_id:'${feedbackId}'`)),
            );

            await Promise.all(deletedFeedbacks);
          }
        }
      }
    }

    // SUC-09 — a dupla é escrita em conjunto (evaluator + non_evaluator), então o reset
    // também desfaz em conjunto: todo id de non_evaluator amarrado a um elo REMOVIDO do
    // evaluator sai junto — o parceiro do elo removido (pit stop grava o parceiro do
    // assessor e do Nível 3 em non_evaluator) e o próprio elo removido quando o swap
    // CON-05 o deixou em non_evaluator. Parceiros de elos que PERMANECEM (incl. o do
    // original_manager) ficam intactos.
    if (Array.isArray(newObj.non_evaluator) && newObj.non_evaluator.length && previousEvaluator.length) {
      const remainingEvaluator: string[] = Array.isArray(newObj.evaluator) ? newObj.evaluator.map(String) : [];
      const removedEvaluators = previousEvaluator.filter(id => !remainingEvaluator.includes(id));

      if (removedEvaluators.length) {
        const detailById = new Map<string, any>();

        for (const detail of [
          ...(Array.isArray(newObj.evaluator_detail) ? newObj.evaluator_detail : []),
          ...(Array.isArray(newObj.non_evaluator_detail) ? newObj.non_evaluator_detail : []),
          ...(Array.isArray(task.evaluator_detail) ? task.evaluator_detail : []),
          ...(Array.isArray(task.non_evaluator_detail) ? task.non_evaluator_detail : []),
        ]) {
          if (detail?._id && !detailById.has(String(detail._id))) {
            detailById.set(String(detail._id), detail);
          }
        }

        const duplaIdsOf = (id: string): string[] => {
          const madDupla = detailById.get(id)?.mad_dupla;
          return Array.isArray(madDupla) ? madDupla.map((md: any) => String(md?._id)).filter(Boolean) : [];
        };

        // Amarrado a um removido = é o removido (resíduo de swap) OU é parceiro dele —
        // checagem pelos dois lados, pois o vínculo de dupla pode estar registrado só num deles.
        const isTiedToRemoved = (id: string) =>
          removedEvaluators.includes(id) ||
          duplaIdsOf(id).some(duplaId => removedEvaluators.includes(duplaId)) ||
          removedEvaluators.some(removedId => duplaIdsOf(removedId).includes(id));

        newObj.non_evaluator = newObj.non_evaluator.filter((id: any) => !isTiedToRemoved(String(id)));
      }
    }

    if (isVacationManager) {
      if (!newObj.substitute_managers) {
        newObj.substitute_managers = [];
      }

      const timestamp = await getServerTime();

      newObj.substitute_managers.push({
        playerId: user?._id,
        playerName: user?.name,
        status: 'reset',
        time: { $date: timestamp },
      });
    }

    await updateAllTasks('reset', newObj, task);
  }

  // Espelho de normal/handleAccept (port de index.tsx:2657-2723) — SelectDupla no lugar do
  // SelectGestor (grava evaluator + non_evaluator).
  async function handleAccept(_item: any, selection?: SelectionWrite) {
    const item = await guardTaskAgainstConcurrentUpdate(_item);
    if (!item) return;

    const managerTypeResult = CheckManagerType({
      user: { ...playerInfo, cod_etapa: madUserCodEtapa(ctx, playerInfo) },
      item,
    });

    const updatedData = { ...item };

    updatedData.status = 'aprovado';
    updatedData.multiplicador = updatedData.multiplicador || 1;
    updatedData.stageNumber = 1;

    if (managerTypeResult === 'equipe') {
      if (!selection) {
        const nextManagers = await getNextManagers(item._id);
        setSelectedDupla({
          task: item,
          cod: [1, 3],
          duplaOptions: buildDuplaOptions(nextManagers, 1),
        });
        setFunctionDupla(`handleAccept`);
        return;
      }

      writeSelection(updatedData, selection);
    }

    if (isVacationManager) {
      if (!updatedData.substitute_managers) {
        updatedData.substitute_managers = [];
      }

      const timestamp = await getServerTime();

      updatedData.substitute_managers.push({
        playerId: user?._id,
        playerName: user?.name,
        status: 'aprovado',
        time: { $date: timestamp },
      });
    }

    const updated = await updateAllTasks('accept', updatedData);

    // CON-06: o modal de feedback só abre quando a apreciação foi de fato computada.
    if (updated && (!!selection || managerTypeResult !== 'equipe')) {
      setGetAction({ ...item, fuction: `validation-accept-${item._id}-finish` });
    }
  }

  // Espelho de normal/handleStop (port de index.tsx:2725-2764) — pit stop DUPLA-AWARE:
  // o gate posicional passa a aceitar o parceiro (isPitStopAssessor), e a parada usa
  // applyAssessorStop.
  async function handleStop(_item: any) {
    const item = await guardTaskAgainstConcurrentUpdate(_item);
    if (!item) return false;

    const updatedData = { ...item };

    // PIT-04 — o assessor (eu OU meu parceiro, R-21) PARA a cadeia.
    if (isPitStopAssessor(ctx, updatedData)) {
      applyAssessorStop(updatedData);
    }

    updatedData.status = 'concluido';

    if (isVacationManager) {
      if (!updatedData.substitute_managers) {
        updatedData.substitute_managers = [];
      }

      const timestamp = await getServerTime();

      updatedData.substitute_managers.push({
        playerId: user?._id,
        playerName: user?.name,
        status: 'concluido',
        time: { $date: timestamp },
      });
    }

    const updated = await updateAllTasks('stop', updatedData);
    return !!updated;
  }

  // Espelho de normal/handleReject (port de index.tsx:2766-2797).
  async function handleReject(_item: any) {
    const item = await guardTaskAgainstConcurrentUpdate(_item);
    if (!item) return;

    const updatedData = { ...item };
    updatedData.status = 'rejeitado';

    // PART-02: devolver NÃO altera a aprovação da quantidade de participantes.

    if (isVacationManager) {
      if (!updatedData.substitute_managers) {
        updatedData.substitute_managers = [];
      }

      const timestamp = await getServerTime();

      updatedData.substitute_managers.push({
        playerId: user?._id,
        playerName: user?.name,
        status: 'rejeitado',
        time: { $date: timestamp },
      });
    }

    await updateAllTasks('reject', updatedData);
  }

  // Espelho de normal/handleDoubled (port de index.tsx:2799-2877) COM o guard em lote da
  // bonificação MAD (CON-04, index.tsx:2845-2858 via shouldGuardBatchOnDouble).
  async function handleDoubled(_item: any) {
    const item = await guardTaskAgainstConcurrentUpdate(_item);
    if (!item) return false;

    if (String(item.original_manager) === String(playerInfo._id) && madUserCodEtapa(ctx, null) === 5) {
      setModalCod5(item);
      return false;
    }

    const multiplicador = typeof item.multiplicador === 'number' ? item.multiplicador * 2 : 1;

    const updatedData = { ...item };
    updatedData.status = 'dobrado';
    updatedData.multiplicador = multiplicador;

    const canPromoteResult = canPromote(ctx, updatedData, item);

    if (!canPromoteResult) {
      updatedData.status = 'concluidoAuto';
    } else if (shouldGuardBatchOnDouble(ctx, item, canPromoteResult)) {
      // CON-04 — há slot de promoção, mas o parceiro concorre no mesmo pool em outra tela:
      // valida contra o servidor. Divergiu → cancela e recarrega (ao reapreciar sobre dados
      // frescos, canPromote dará false e a realização será concluída automaticamente).
      const guard = await guardBatchAgainstConcurrentUpdate(ctx, filteredData as any[]);
      if (!guard.ok) {
        void reload();
        return false;
      }
    }

    if (isVacationManager) {
      if (!updatedData.substitute_managers) {
        updatedData.substitute_managers = [];
      }

      const timestamp = await getServerTime();

      updatedData.substitute_managers.push({
        playerId: user?._id,
        playerName: user?.name,
        status: 'dobrado',
        time: { $date: timestamp },
      });
    }

    const updated = await updateAllTasks('double', updatedData);
    return !!updated;
  }

  // Espelho de normal/handlePromote (port de index.tsx:2879-3020) — pit stop MAD (PIT-01/03)
  // via pitstop.ts (atrás da flag MAD_PITSTOP_ENABLED) e SelectDupla no lugar do SelectGestor.
  async function handlePromote(_item: any, selection?: SelectionWrite, multiplicador?: number) {
    const item = await guardTaskAgainstConcurrentUpdate(_item);
    if (!item) return;

    const managerTypeResult = CheckManagerType({
      user: { ...playerInfo, cod_etapa: madUserCodEtapa(ctx, playerInfo) },
      item,
    });

    const nextManagers = await getNextManagers(item._id);

    let shouldShowFeedback = false;

    const updatedData = { ...item };

    let stageNumber = updatedData.stageNumber;

    if (getTaskStepLocal(item) === 3) {
      stageNumber = 2;
    } else if (typeof getTaskStepLocal(item) === 'number' && (getTaskStepLocal(item) as number) >= 4) {
      stageNumber = 3;
    }

    let managerProsseguir: null | string = null;

    if (shouldEnterPitStop(ctx, playerInfo, item, nextManagers) && stageNumber === 3) {
      // PIT-01 — entrada no pit stop MAD. applyPitStopOnPromote consolida
      // prosseguir_gst='next', prosseguir_idGestor, o push em prosseguir_managers e o push
      // do próximo assessor em evaluator (por isso o ramo downstream não re-empurra).
      applyPitStopOnPromote(updatedData, nextManagers);
    } else if (isPitStopAssessor(ctx, updatedData) && stageNumber === 3) {
      // PIT-03 — o assessor (dupla-aware) aprova a continuação. applyAssessorApprove
      // consolida approved/who_approved e o push do nível 3 (ou gestor, fallback) em evaluator.
      managerProsseguir = nextManagers && nextManagers.nivel3 ? nextManagers.nivel3._id : playerInfo.gestor;
      applyAssessorApprove(updatedData, ctx, nextManagers);
    }

    updatedData.status = 'promovido';
    updatedData.stageNumber = stageNumber;

    if (!updatedData.evaluator) {
      updatedData.evaluator = [];
    }

    if (managerTypeResult === 'diretoria') {
      updatedData.multiplicador = multiplicador;
      updatedData.status = typeof multiplicador === 'number' && multiplicador >= 16 ? 'selo' : 'concluido';
    } else if (playerInfo.gestor || managerProsseguir) {
      if (typeof managerProsseguir === 'string') {
        // applyAssessorApprove já registrou o nível 3 em evaluator (PIT-03).
        shouldShowFeedback = true;
      } else {
        if (updatedData.prosseguir_gst === 'next') {
          // applyPitStopOnPromote já registrou o próximo assessor em evaluator (PIT-01).
          shouldShowFeedback = true;
        } else {
          if (
            madUserCodEtapa(ctx, null) === 4 &&
            String(updatedData.original_manager) === String(playerInfo._id) &&
            activeStageObj &&
            activeStageObj.date_evaluation_step4_start &&
            realDatetime < activeStageObj.date_evaluation_step4_start
          ) {
            shouldShowFeedback = true;
          } else if (!selection) {
            setSelectedDupla({
              task: item,
              cod: stageNumber === 2 ? [4] : [5],
              duplaOptions: buildDuplaOptions(nextManagers, stageNumber === 2 ? 2 : 3),
            });
            setFunctionDupla(`handlePromote`);
            return;
          }

          writeSelection(updatedData, selection);
        }
      }
    }

    if (isVacationManager) {
      if (!updatedData.substitute_managers) {
        updatedData.substitute_managers = [];
      }

      const timestamp = await getServerTime();

      updatedData.substitute_managers.push({
        playerId: user?._id,
        playerName: user?.name,
        status: updatedData.status,
        time: { $date: timestamp },
      });
    }

    // om cod4 promovendo antes da step4: o evaluator não é gravado (o executivo original
    // continua o avaliador corrente).
    if (
      madUserCodEtapa(ctx, null) === 4 &&
      String(updatedData.original_manager) === String(playerInfo._id) &&
      getActiveEvaluationStep &&
      getActiveEvaluationStep < 4
    ) {
      delete updatedData.evaluator;
    }

    if (!!selection) {
      shouldShowFeedback = true;
    }

    const updated = await updateAllTasks('promote', updatedData);

    // CON-06: o modal de feedback só abre quando a apreciação foi de fato computada.
    if (updated && shouldShowFeedback) {
      setGetAction({ ...item, fuction: `validation-promote-${item._id}-finish` });
    }
  }

  // Espelho de normal/handleAward (port de index.tsx:3022-3063).
  async function handleAward(_item: any, badge: 'uau' | 'mandou' | 'valeu') {
    const item = await guardTaskAgainstConcurrentUpdate(_item);
    if (!item) return false;

    const status = badge === 'uau' ? 'selo' : badge;

    const updatedData = { ...item };
    updatedData.status = status;
    updatedData.multiplicador = status === 'selo' ? 16 : status === 'mandou' ? 8 : 5;

    if (isVacationManager) {
      if (!updatedData.substitute_managers) {
        updatedData.substitute_managers = [];
      }

      const timestamp = await getServerTime();

      updatedData.substitute_managers.push({
        playerId: user?._id,
        playerName: user?.name,
        status: status,
        time: { $date: timestamp },
      });
    }

    const updated = await updateAllTasks('award', updatedData);
    return !!updated;
  }

  // Espelho de normal/handleSubmitCod5 (port de index.tsx:3066-3111).
  async function handleSubmitCod5(item: any) {
    const guard = await guardTaskAgainstConcurrentUpdate(modalCod5);
    if (!guard) {
      setModalCod5(undefined);
      return false;
    }

    setModalCod5(undefined);

    let action = 'double';

    if (item.status === 'valeu' || item.status === 'mandou' || item.status === 'selo') {
      action = 'award';
    }

    const updated = await updateAllTasks(action, item);
    if (!updated) return false;

    document.dispatchEvent(
      new CustomEvent('open:modal-feedback', {
        detail: {
          _id: item._id,
        },
      }),
    );
    return true;
  }

  // Traduz a opção escolhida no SelectDupla nos papéis de escrita (SUC-09) — usado pela
  // página ao confirmar a dupla.
  function resolveDuplaSelection(option: DuplaOption): SelectionWrite | null {
    return resolveSelectionWrite(option);
  }

  return {
    // estados de orquestração
    selectedDupla,
    setSelectedDupla,
    functionDupla,
    setFunctionDupla,
    getAction,
    setGetAction,
    modalCod5,
    setModalCod5,
    // ações
    updateAllTasks,
    handleAccept,
    handleStop,
    handleReject,
    handleDoubled,
    handlePromote,
    handleAward,
    handleReset,
    handleSubmitCod5,
    resolveDuplaSelection,
  };
}
